import { DeleteOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { Alert, App, Input, Space, Spin } from 'antd';
import type React from 'react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import type { GetOrderByIdWithDriversWarehousesQuery } from '@/generated/graphql/graphql';
import { OrderDrawerFormPositionsModal } from '../OrderDrawer/OrderDrawerForm/components/OrderDrawerFormPositionsModal';
import { useCalcOrderPrice } from '../query/orders.hooks';

interface Product {
  __typename?: 'product';
  id: string;
  name: string;
}

export interface Position {
  __typename?: 'order_position';
  id: number;
  quantity: number;
  productID: string;
  totalPrice?: number | null;
  product: Product;
}

interface OrderPositionsProps {
  isManagerOrSeniorOperator: boolean;
  positions: Position[];
  setPositions: React.Dispatch<React.SetStateAction<Position[]>>;
  orderForm: GetOrderByIdWithDriversWarehousesQuery;
  isDisabled?: boolean;
}

type ServerOrderItem = {
  productId: string;
  qty: number;
  totalPrice: number;
  price?: number;
};

export const OrderPositions: FC<OrderPositionsProps> = ({
  isManagerOrSeniorOperator,
  positions,
  setPositions,
  orderForm,
  isDisabled = false,
}) => {
  const { message } = App.useApp();
  const order = orderForm?.order_by_pk;
  if (!order) {
    throw new Error('Order not found');
  }
  const calcOrderPrice = useCalcOrderPrice();

  const [itemsPrice, setItemsPrice] = useState(0);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [recalcIncorrectMessage, setRecalcIncorrectMessage] = useState<string | undefined>();
  const [addProductModalVisible, setAddProductModalVisible] = useState(false);
  const [calcError, setCalcError] = useState<string | undefined>();
  const [lockedPositionIds, setLockedPositionIds] = useState<Set<number>>(new Set());

  const currentOrderIdRef = useRef<number>(order.id);
  const isCalculatingRef = useRef<boolean>(false);
  const isInitialLoadRef = useRef<boolean>(true);
  const prevPositionsRef = useRef<Position[]>([]);

  useEffect(() => {
    currentOrderIdRef.current = order.id;
    if (isCalculatingRef.current) {
      console.log(`Order changed to ${order.id}, marking current calculation as stale`);
      isCalculatingRef.current = false;
    }
    isInitialLoadRef.current = true;
    prevPositionsRef.current = [];
  }, [order.id]);

  const isOrderEditingBlocked = order?.vendorDatalinkId !== 5;
  const canEditPositions = isManagerOrSeniorOperator && !isOrderEditingBlocked && !isDisabled;

  const calculateFromOrderData = useCallback(
    (positionsToUse: Position[]) => {
      const sumItemsPrice = positionsToUse.reduce((sum, p) => sum + (p.totalPrice ?? 0), 0);
      setItemsPrice(sumItemsPrice);

      const totalOrderPrice = order?.totalCents || 0;
      const calculatedDeliveryPrice = totalOrderPrice - sumItemsPrice;
      setDeliveryPrice(Math.max(0, calculatedDeliveryPrice));
      setRecalcIncorrectMessage(undefined);
    },
    [order?.totalCents]
  );

  const mapPositionsToRemote = useCallback((positions: Position[]) => {
    return positions.map((position) => ({
      productId: position.productID,
      qty: position.quantity,
    }));
  }, []);

  const groupServerItemsByProductId = useCallback((serverItems: ServerOrderItem[]) => {
    const byProductId = new Map<string, ServerOrderItem[]>();
    for (const item of serverItems) {
      const arr = byProductId.get(item.productId) ?? [];
      arr.push(item);
      byProductId.set(item.productId, arr);
    }
    return byProductId;
  }, []);

  const createProductLookup = useCallback(() => {
    const map = new Map<string, Product>();
    const list = (orderForm as { product?: Product[] })?.product || [];
    for (const prod of list as Product[]) {
      map.set(prod.id, prod);
    }
    return map;
  }, [orderForm]);

  const createPositionFromServerItem = useCallback(
    (
      serverItem: ServerOrderItem,
      basePosition: Position | undefined,
      productLookup: Map<string, Product>,
      nowBase: number,
      counter: number
    ): { position: Position; newCounter: number } => {
      if (!basePosition) {
        const prod = productLookup.get(serverItem.productId) || {
          id: serverItem.productId,
          name: 'Unknown',
        };
        const position: Position = {
          id: nowBase + counter,
          product: prod,
          productID: serverItem.productId,
          quantity: serverItem.qty,
          totalPrice: serverItem.totalPrice,
          __typename: 'order_position',
        } as Position;
        return { position, newCounter: counter + 1 };
      }

      const position: Position = {
        ...basePosition,
        quantity: serverItem.qty,
        totalPrice: serverItem.totalPrice,
      };
      return { position, newCounter: counter };
    },
    []
  );

  const buildResultPositions = useCallback(
    (
      byProductId: Map<string, ServerOrderItem[]>,
      positionsToUse: Position[],
      productLookup: Map<string, Product>
    ) => {
      const newLockedIds = new Set<number>();
      const resultPositions: Position[] = [];
      const nowBase = Date.now();
      let counter = 0;

      Array.from(byProductId.entries()).forEach(([productId, serverGroup]) => {
        const existingGroup = positionsToUse.filter((p) => p.productID === productId);

        for (let i = 0; i < serverGroup.length; i++) {
          const serverItem = serverGroup[i];
          const basePosition = existingGroup[i];
          const { position, newCounter } = createPositionFromServerItem(
            serverItem,
            basePosition,
            productLookup,
            nowBase,
            counter
          );
          counter = newCounter;

          resultPositions.push(position);

          if ((serverItem.totalPrice ?? 0) === 0) {
            newLockedIds.add(position.id);
          }
        }
      });

      return { resultPositions, newLockedIds };
    },
    [createPositionFromServerItem]
  );

  const checkPositionsChanged = useCallback(
    (resultPositions: Position[], positionsToUse: Position[]) => {
      if (resultPositions.length !== positionsToUse.length) {
        return true;
      }

      return resultPositions.some((p, idx) => {
        const prev = positionsToUse[idx];
        if (!prev) return true;

        return (
          prev.id !== p.id ||
          prev.productID !== p.productID ||
          prev.quantity !== p.quantity ||
          (prev.totalPrice ?? 0) !== (p.totalPrice ?? 0)
        );
      });
    },
    []
  );

  const updatePrices = useCallback((resultPositions: Position[], deliveryPrice: number) => {
    const sumItemsPrice = resultPositions.reduce((sum, p) => sum + (p.totalPrice ?? 0), 0);
    setItemsPrice(sumItemsPrice);
    setDeliveryPrice(deliveryPrice);
  }, []);

  const validateOrderCalculation = useCallback((currentOrderId: number): boolean => {
    if (!isCalculatingRef.current || currentOrderIdRef.current !== currentOrderId) {
      console.warn(
        `Order changed during price calculation. Expected: ${currentOrderId}, Current: ${currentOrderIdRef.current}`
      );
      return false;
    }
    return true;
  }, []);

  const processServerResponse = useCallback(
    (serverItems: ServerOrderItem[], positionsToUse: Position[], newDeliveryPrice: number) => {
      const byProductId = groupServerItemsByProductId(serverItems);
      const productLookup = createProductLookup();
      const { resultPositions, newLockedIds } = buildResultPositions(
        byProductId,
        positionsToUse,
        productLookup
      );

      updatePrices(resultPositions, newDeliveryPrice);

      const positionsChanged = checkPositionsChanged(resultPositions, positionsToUse);
      setLockedPositionIds(newLockedIds);

      if (positionsChanged) {
        prevPositionsRef.current = resultPositions;
        setPositions(resultPositions);
      }
    },
    [
      groupServerItemsByProductId,
      createProductLookup,
      buildResultPositions,
      checkPositionsChanged,
      updatePrices,
      setPositions,
    ]
  );

  const recalcOrderPrice = useCallback(
    async (positionsToUse: Position[]) => {
      if (!positionsToUse.length) {
        setItemsPrice(0);
        setDeliveryPrice(0);
        return;
      }

      if (isOrderEditingBlocked) {
        calculateFromOrderData(positionsToUse);
        return;
      }

      const totalQuantity = positionsToUse.reduce((sum, p) => sum + p.quantity, 0);
      setRecalcIncorrectMessage(
        totalQuantity === 2 ? 'The total quantity in the basket cannot be 2' : undefined
      );

      const currentOrderId = order.id;
      isCalculatingRef.current = true;
      setCalcError(undefined);

      try {
        const response = await calcOrderPrice.mutateAsync({
          orderID: order.id,
          umbrellaID: order.umbrellaID ?? '',
          orderItems: mapPositionsToRemote(positionsToUse),
        });

        if (!validateOrderCalculation(currentOrderId)) {
          return;
        }

        const serverItems =
          (response?.recalcOrder?.orderItems as ServerOrderItem[] | null | undefined) ?? [];
        const newDeliveryPrice = response?.recalcOrder?.deliveryPrice || 0;
        processServerResponse(serverItems, positionsToUse, newDeliveryPrice);
      } catch (error) {
        console.error('Failed to calculate order price:', error);
        setCalcError(`Failed to calculate order price`);
      } finally {
        isCalculatingRef.current = false;
      }
    },
    [
      calcOrderPrice,
      order.id,
      calculateFromOrderData,
      mapPositionsToRemote,
      isOrderEditingBlocked,
      order.umbrellaID,
      validateOrderCalculation,
      processServerResponse,
    ]
  );

  const recalcDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const havePriceAffectingChanges = useCallback((prev: Position[], next: Position[]) => {
    if (prev.length !== next.length) return true;

    const prevMap = new Map(prev.map((p) => [p.id, p.quantity]));
    for (const pos of next) {
      if (!prevMap.has(pos.id) || prevMap.get(pos.id) !== pos.quantity) {
        return true;
      }
    }
    return false;
  }, []);

  const handleInitialLoad = useCallback(
    (positionsToUse: Position[]) => {
      if (positionsToUse.length > 0) {
        calculateFromOrderData(positionsToUse);
      } else {
        setItemsPrice(0);
        setDeliveryPrice(0);
      }
      isInitialLoadRef.current = false;
    },
    [calculateFromOrderData]
  );

  const handleUserChanges = useCallback(
    (positionsToUse: Position[]) => {
      if (positionsToUse.length > 0) {
        void recalcOrderPrice(positionsToUse);
      } else {
        setItemsPrice(0);
        setDeliveryPrice(0);
      }
    },
    [recalcOrderPrice]
  );

  const handleNoChanges = useCallback(
    (positionsToUse: Position[]) => {
      const sumItemsPrice = positionsToUse.reduce((sum, p) => sum + (p.totalPrice ?? 0), 0);
      setItemsPrice(sumItemsPrice);

      if (isOrderEditingBlocked) {
        const totalOrderPrice = order?.totalCents || 0;
        const calculatedDeliveryPrice = totalOrderPrice - sumItemsPrice;
        setDeliveryPrice(Math.max(0, calculatedDeliveryPrice));
      }
    },
    [isOrderEditingBlocked, order?.totalCents]
  );

  useEffect(() => {
    if (recalcDebounceRef.current) {
      clearTimeout(recalcDebounceRef.current);
    }

    recalcDebounceRef.current = setTimeout(() => {
      const isInitialLoad = isInitialLoadRef.current;
      const hasUserChanges = havePriceAffectingChanges(prevPositionsRef.current, positions);

      if (isInitialLoad) {
        handleInitialLoad(positions);
      } else if (hasUserChanges) {
        handleUserChanges(positions);
      } else {
        handleNoChanges(positions);
      }

      prevPositionsRef.current = positions;
    }, 400);

    return () => {
      if (recalcDebounceRef.current) {
        clearTimeout(recalcDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, havePriceAffectingChanges, handleInitialLoad, handleUserChanges, handleNoChanges]);

  const isGlobalInputDisabled = calcOrderPrice.isPending || !canEditPositions || isDisabled;

  const onChangeQuantity = (quantity: number, positionId: number) => {
    if (isOrderEditingBlocked || isGlobalInputDisabled || isDisabled) return;
    if (lockedPositionIds.has(positionId)) return;

    const updatedPositions = positions.map((p) => (p.id === positionId ? { ...p, quantity } : p));
    setPositions(updatedPositions);
  };

  const onDeletePosition = (positionId: number) => {
    if (isOrderEditingBlocked || isGlobalInputDisabled || isDisabled) return;
    if (lockedPositionIds.has(positionId)) return;

    if (positions.length <= 1) {
      message.error('The order must contain at least one line item!');
      return;
    }
    const updatedPositions = positions.filter((p) => p.id !== positionId);
    setPositions(updatedPositions);
  };

  const onAddToOrderPosition = (product: Product) => {
    if (isOrderEditingBlocked || isDisabled) return;

    const updatedPositions = [
      ...positions,
      {
        id: Date.now(),
        product,
        productID: product.id,
        quantity: 1,
        totalPrice: 0,
      } as Position,
    ];
    setPositions(updatedPositions);
    setAddProductModalVisible(false);
  };

  const [productsWithoutOrder, setProductsWithoutOrder] = useState<Product[]>([]);

  useEffect(() => {
    if (!orderForm?.order_by_pk || !orderForm.product) {
      setProductsWithoutOrder([]);
      return;
    }

    const existingProductsIds = positions.map((p) => p.productID) || [];
    const productsNotInOrder = orderForm.product.filter(
      (product) => !existingProductsIds.includes(product.id)
    );

    setProductsWithoutOrder(productsNotInOrder);
  }, [orderForm, positions]);

  const finalTotal = itemsPrice + deliveryPrice;

  const sortPositionsByBonusLast = (pos: Position[]) =>
    [...pos].sort((a, b) => {
      const aBonus = (a.totalPrice ?? 0) === 0;
      const bBonus = (b.totalPrice ?? 0) === 0;
      return aBonus === bBonus ? 0 : aBonus ? 1 : -1;
    });

  return (
    <>
      {isOrderEditingBlocked && (
        <Alert
          title="Position management is blocked for this order"
          type="warning"
          showIcon
          style={{ marginBottom: '16px', marginTop: '16px' }}
        />
      )}
      <div className="ant-list ant-list-split">
        <div className="ant-list-items">
          {sortPositionsByBonusLast(positions).map((position) => {
            const isLocked = lockedPositionIds.has(position.id);
            return (
              <div className="order-drawer__item" key={position.id}>
                <Space>{position.product.name}</Space>
                <Space>{position.totalPrice}</Space>
                <Space>
                  <Input
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10) || 1;
                      onChangeQuantity(value, position.id);
                    }}
                    required
                    placeholder="Qnt."
                    type="number"
                    value={position.quantity.toString()}
                    min={1}
                    disabled={isGlobalInputDisabled || isLocked}
                  />
                  <DeleteOutlined
                    onClick={() => {
                      onDeletePosition(position.id);
                    }}
                    style={
                      isGlobalInputDisabled || isLocked
                        ? { pointerEvents: 'none', opacity: 0.4 }
                        : {}
                    }
                  />
                </Space>
              </div>
            );
          })}
          <div
            className="order-drawer__item"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Space>Delivery Price:</Space>
            {!isOrderEditingBlocked && calcOrderPrice.isPending ? <Spin /> : <b>{deliveryPrice}</b>}
          </div>
          <div
            className="order-drawer__item"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Space>Total:</Space>
            {recalcIncorrectMessage ? (
              <Space>
                <span style={{ color: 'red' }}>{recalcIncorrectMessage}</span>
              </Space>
            ) : !isOrderEditingBlocked && calcOrderPrice.isPending ? (
              <Spin />
            ) : (
              <b>{finalTotal}</b>
            )}
          </div>
        </div>
      </div>
      {calcError && (
        <Alert
          title={calcError}
          type="error"
          showIcon
          closable
          onClose={() => setCalcError(undefined)}
          style={{ marginTop: '8px', marginBottom: '8px' }}
        />
      )}
      {canEditPositions && (
        <Space>
          <PlusSquareOutlined
            onClick={() => {
              if (!isGlobalInputDisabled) setAddProductModalVisible(true);
            }}
            style={isGlobalInputDisabled ? { pointerEvents: 'none', opacity: 0.4 } : {}}
          />
        </Space>
      )}
      <OrderDrawerFormPositionsModal
        addProductModalVisible={addProductModalVisible}
        setAddProductModalVisible={setAddProductModalVisible}
        onAddToOrderPosition={onAddToOrderPosition}
        productsWithoutOrder={productsWithoutOrder}
      />
    </>
  );
};
